import { useEffect, useRef, useMemo } from "react";
import { StaticImageData } from "next/image";
import classNames from "classnames";
import Plyr from "plyr";

import { translate } from "@ascendnow/shared";

import {
  isYoutube,
  youtube_parser,
  getVideoExtensionFormat,
} from "./AscVideoPlayer.helper";
import styles from "./AscVideoPlayer.module.scss";
import BannerImage from "../../assets/images/module-image-1.webp";

export type VideoPlayerCaption = {
  label: string;
  srclang: string;
  src: string;
};

export type AscVideoPlayerNewProps = {
  url: string;
  captions?: VideoPlayerCaption[];
  options?: Plyr.Options;
  thumbnailSrc?: string;
  title?: string;
  className?: string;
  disabled?: boolean;
  height?: string | number;
};

export default function AscVideoPlayerNew({
  url,
  captions,
  options,
  title,
  thumbnailSrc,
  className,
  disabled = false,
  height,
}: AscVideoPlayerNewProps) {
  const plyrInstance = useRef<Plyr | null>(null);

  const defaultOptions: Plyr.Options = useMemo(
    () => ({
      settings: ["quality", "speed", "loop", "captions"],
      controls: [
        "captions",
        "restart",
        "rewind",
        "play",
        "fast-forward",
        "progress",
        "current-time",
        "duration",
        "mute",
        "volume",
        "settings",
        "fullscreen",
      ],
      captions: { active: true, update: true },
      tooltips: {
        controls: true,
        seek: true,
      },
      youtube: {
        rel: 0,
        showinfo: 0,
      },
      speed: {
        selected: 1,
        options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      },
      previewThumbnails: {
        enabled: false,
      },
      mediaMetadata: {
        title: title,
        artwork: [
          {
            src:
              typeof BannerImage === "object"
                ? (BannerImage as StaticImageData)["src"]
                : "",
            type: "image/jpg",
          },
        ],
      },
      quality: { default: 576, options: [1080, 720, 576, 480, 360, 240] },
    }),
    [title, thumbnailSrc]
  );

  const videoSource = useMemo(() => {
    let videoSource: Plyr.Source;
    if (url && isYoutube(url)) {
      videoSource = {
        src: url ? youtube_parser(url) : "",
        type: "video/mp4",
        provider: "youtube",
      };
    } else if (String(url).includes("vimeo")) {
      videoSource = {
        src: url ?? "",
        type: "video/mp4",
        provider: "vimeo",
      };
    } else {
      videoSource = {
        src: url ?? "",
        type: url?.includes("blob")
          ? "video/mp4"
          : `video/${getVideoExtensionFormat(url)}`,
        provider: "html5",
      };
    }
    return {
      type: "video" as Plyr.MediaType,
      title,
      sources: !disabled && videoSource && videoSource.src ? [videoSource] : [],
      tracks:
        captions && captions.length > 0
          ? captions?.map((el) => ({
              ...el,
              kind: "captions" as Plyr.TrackKind,
            }))
          : [],
      poster: thumbnailSrc
        ? thumbnailSrc
        : typeof BannerImage === "object"
        ? (BannerImage as StaticImageData)["src"]
        : "",
    };
  }, [title, url, captions, thumbnailSrc, disabled]);

  useEffect(() => {
    if (url) {
      setTimeout(() => {
        plyrInstance.current = new Plyr(".js-plyr", {
          ...defaultOptions,
          ...options,
        });
        plyrInstance.current.source = videoSource;
      }, 100);
    }

    return () => {
      if (plyrInstance.current && url) {
        plyrInstance.current.destroy();
      }
    };
  }, [options, defaultOptions, videoSource, url]);

  return (
    <div
      data-testid="asc-video-player-new"
      className={classNames(styles.ground, className)}
    >
      {url ? (
        <video
          className="js-plyr plyr"
          // @ts-ignore
          crossOrigin="true"
          height={height}
          tabIndex={0}
        />
      ) : (
        <div className={styles.emptyContainer} tabIndex={0}>
          <span className={styles.empty}>
            {translate("videoSourceNotProvided")}
          </span>
        </div>
      )}
    </div>
  );
}
